import {CurrentUser} from "@cling/lib.web.resources"
import {
    SYSTEM_BOARD_TYPES,
    derive_system_board_uid,
    SyncEntityType,
    Account,
    AccountUID,
    AccountSettings,
    AccountAttributes,
    BoardUID,
    AccountAuth,
    AccountAnalytics,
    Board,
} from "@cling/lib.shared.model"
import type {LocalCache} from "@cling/lib.web.resources/local_cache"
import {log} from "@cling/lib.shared.logging"
import type {UID} from "@cling/lib.shared.types/common"
import {sleep} from "@cling/lib.shared.utils"

/**
 * This does these things:
 * - Wait until all data to construct a `CurrentUser` is available.
 * - Wait until all system-boards are available.
 * - Trigger sync of missing data.
 */
export async function sync_initial_state({
    account_uid,
    caches,
    request_sync,
    board_uid_hint,
}: {
    account_uid: AccountUID
    caches: {
        [SyncEntityType.account]: LocalCache<AccountUID, Account>
        [SyncEntityType.account_attributes]: LocalCache<AccountUID, AccountAttributes>
        [SyncEntityType.account_settings]: LocalCache<AccountUID, AccountSettings>
        [SyncEntityType.account_auth]: LocalCache<AccountUID, AccountAuth>
        [SyncEntityType.account_analytics]: LocalCache<AccountUID, AccountAnalytics>
        [SyncEntityType.board]: LocalCache<BoardUID, Board>
    }
    request_sync: (uid: UID, type: SyncEntityType) => void
    board_uid_hint?: BoardUID
}): Promise<CurrentUser> {
    let backoff = 10
    let attempt = 0
    function request_account_sync() {
        request_sync(account_uid, SyncEntityType.account)
        request_sync(account_uid, SyncEntityType.account_attributes)
        request_sync(account_uid, SyncEntityType.account_settings)
        request_sync(account_uid, SyncEntityType.account_auth)
        request_sync(account_uid, SyncEntityType.account_analytics)
    }
    function request_board_sync() {
        for (const x of SYSTEM_BOARD_TYPES) {
            request_sync(derive_system_board_uid(account_uid, x), SyncEntityType.board)
        }
        if (board_uid_hint) {
            request_sync(board_uid_hint, SyncEntityType.board)
        }
    }
    request_account_sync()
    request_board_sync()
    for (;;) {
        try {
            let current_user: CurrentUser
            try {
                const account = caches[SyncEntityType.account].get(account_uid)
                const account_analytics = caches[SyncEntityType.account_analytics].get(account_uid)
                const account_attributes =
                    caches[SyncEntityType.account_attributes].get(account_uid)
                const account_settings = caches[SyncEntityType.account_settings].get(account_uid)
                const account_auth = caches[SyncEntityType.account_auth].get(account_uid)
                current_user = new CurrentUser({
                    account,
                    account_attributes,
                    account_analytics,
                    account_settings,
                    account_auth,
                    is_new: false,
                })
            } catch (error) {
                // Ok, we could not find anything in the caches, wait until they are magically
                // filled (by the worker) ...
                if (attempt >= 10 && attempt % 10 === 0) {
                    log.debug("Initial account still not sent by worker -- re-requesting it", {
                        attempt,
                    })
                    request_account_sync()
                }
                throw error
            }
            // Make sure all system boards are in sync ...
            try {
                for (const x of SYSTEM_BOARD_TYPES) {
                    const system_board_uid = derive_system_board_uid(account_uid, x)
                    caches[SyncEntityType.board].get(system_board_uid)
                }
                // We explicitly don't check if `board_uid_hint` is found, because - as the
                // name suggests - it is only a hint ...
            } catch (error) {
                if (attempt >= 10 && attempt % 10 === 0) {
                    log.debug("Initial boards still not sent by worker -- re-requesting them", {
                        attempt,
                    })
                    request_board_sync()
                }
                throw error
            }
            return current_user
        } catch {
            backoff = Math.min(137, backoff * 1.5)
            attempt += 1
            await sleep(backoff)
        }
    }
}
